import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../components/Modal';
import {
  getZones, createZone, deactivateZone,
  getBins, createBin, deactivateBin, getBinItems
} from '../api/warehouse';
import { useSizes } from '../api/meta';

const BIN_TYPES = [
  { value: 'primary_storage', label: 'Primary Storage' },
  { value: 'mini_pack_storage', label: 'Mini-Pack Storage' },
  { value: 'repair', label: 'Repair' },
  { value: 'ironing', label: 'Ironing' },
  { value: 'dispatch', label: 'Dispatch' },
];

function sortByZoddleSize(items, order) {
  return [...items].sort((a, b) => {
    const ai = order.indexOf(a.size);
    const bi = order.indexOf(b.size);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

function OccupancyBar({ current, capacity }) {
  const pct = capacity > 0 ? Math.min((current / capacity) * 100, 100) : 0;
  const isOverflow = current > capacity;
  const barColor = isOverflow ? 'bg-zoddle-pink' : pct > 80 ? 'bg-yellow-400' : 'bg-zoddle-green';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-2 min-w-16">
        <div className={`h-2 rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-medium whitespace-nowrap ${isOverflow ? 'text-zoddle-pink' : 'text-gray-500'}`}>
        {current}/{capacity}{isOverflow && ' ⚠'}
      </span>
    </div>
  );
}

export default function Warehouse() {
  const zoddleSizes = useSizes();
  const [activeTab, setActiveTab] = useState('zones');
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [filterZoneType, setFilterZoneType] = useState('');
  const [showNewZoneModal, setShowNewZoneModal] = useState(false);
  const [zoneRows, setZoneRows] = useState([{ gender: 'girl', size: '2-3Y', zone_type: 'primary_storage' }]);
  const [zoneError, setZoneError] = useState('');
  const [zoneSubmitting, setZoneSubmitting] = useState(false);
  const [bins, setBins] = useState([]);
  const [binsLoading, setBinsLoading] = useState(true);
  const [filterZone, setFilterZone] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showNewBinModal, setShowNewBinModal] = useState(false);
  const [newBin, setNewBin] = useState({ label: '', bin_type: 'primary_storage', zone_id: '', capacity: 50 });
  const [binError, setBinError] = useState('');
  const [binSubmitting, setBinSubmitting] = useState(false);
  const [selectedBin, setSelectedBin] = useState(null);
  const [binItems, setBinItems] = useState([]);
  const [binItemsLoading, setBinItemsLoading] = useState(false);

  const loadZones = useCallback(async () => {
    setZonesLoading(true);
    try {
      const filters = {};
      if (filterZoneType) filters.zone_type = filterZoneType;
      const data = await getZones(filters);
      setZones(sortByZoddleSize(data, zoddleSizes));
    } catch (e) { console.error(e); }
    finally { setZonesLoading(false); }
  }, [filterZoneType, zoddleSizes]);

  const loadBins = useCallback(async () => {
    setBinsLoading(true);
    try {
      const filters = {};
      if (filterZone) filters.zone_id = filterZone;
      if (filterType) filters.bin_type = filterType;
      const data = await getBins(filters);
      const sorted = [...data].sort((a, b) => {
        const zA = zones.find(z => z.zone_id === a.zone_id);
        const zB = zones.find(z => z.zone_id === b.zone_id);
        const ai = zA ? zoddleSizes.indexOf(zA.size) : 999;
        const bi = zB ? zoddleSizes.indexOf(zB.size) : 999;
        return ai - bi;
      });
      setBins(sorted);
    } catch (e) { console.error(e); }
    finally { setBinsLoading(false); }
  }, [filterZone, filterType, zones, zoddleSizes]);

  useEffect(() => { loadZones(); }, [loadZones]);
  useEffect(() => { if (zones.length >= 0) loadBins(); }, [loadBins]);

  function addZoneRow() { setZoneRows([...zoneRows, { gender: 'girl', size: '2-3Y', zone_type: 'primary_storage' }]); }
  function updateZoneRow(i, field, value) { const u = [...zoneRows]; u[i][field] = value; setZoneRows(u); }
  function removeZoneRow(i) { setZoneRows(zoneRows.filter((_, idx) => idx !== i)); }

  async function handleCreateZones() {
    setZoneError(''); setZoneSubmitting(true);
    try {
      for (const row of zoneRows) await createZone(row);
      setShowNewZoneModal(false);
      setZoneRows([{ gender: 'girl', size: '2-3Y', zone_type: 'primary_storage' }]);
      loadZones();
    } catch (e) { setZoneError(e.response?.data?.detail || 'Failed to create zone'); }
    finally { setZoneSubmitting(false); }
  }

  async function handleDeactivateZone(zoneId, label) {
    if (!window.confirm(`Deactivate zone ${label}?`)) return;
    try { await deactivateZone(zoneId); loadZones(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed to deactivate zone'); }
  }

  function handleSelectZone(zone) { setFilterZone(zone.zone_id); setFilterType(''); setActiveTab('bins'); }

  async function handleCreateBin() {
    setBinError(''); setBinSubmitting(true);
    try {
      await createBin({ label: newBin.label, bin_type: newBin.bin_type, capacity: Number(newBin.capacity), zone_id: newBin.zone_id || null });
      setShowNewBinModal(false);
      setNewBin({ label: '', bin_type: 'primary_storage', zone_id: '', capacity: 50 });
      loadBins();
    } catch (e) { setBinError(e.response?.data?.detail || 'Failed to create bin'); }
    finally { setBinSubmitting(false); }
  }

  async function handleDeactivateBin(binId, label) {
    if (!window.confirm(`Deactivate bin ${label}?`)) return;
    try { await deactivateBin(binId); loadBins(); if (selectedBin?.bin_id === binId) setSelectedBin(null); }
    catch (e) { alert(e.response?.data?.detail || 'Failed to deactivate bin'); }
  }

  async function handleSelectBin(bin) {
    setSelectedBin(bin); setBinItemsLoading(true);
    try { const data = await getBinItems(bin.bin_id); setBinItems(data.items || []); }
    catch (e) { setBinItems([]); }
    finally { setBinItemsLoading(false); }
  }

  function getZoneLabel(zoneId) { const z = zones.find(z => z.zone_id === zoneId); return z ? z.label : '—'; }
  function getBinCountForZone(zoneId) { return bins.filter(b => b.zone_id === zoneId).length; }
  function getItemCountForZone(zoneId) { return bins.filter(b => b.zone_id === zoneId).reduce((s, b) => s + (b.current_occupancy || 0), 0); }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-black text-gray-800">Warehouse Management</h2>
        <p className="text-gray-500 text-sm mt-1">Zones and Bins</p>
      </div>
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {['zones','bins'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all capitalize
              ${activeTab === tab ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'zones' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <select value={filterZoneType} onChange={(e) => setFilterZoneType(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">All Types</option>
              <option value="primary_storage">Primary Storage</option>
              <option value="mini_pack_storage">Mini-Pack Storage</option>
            </select>
            {filterZoneType && <button onClick={() => setFilterZoneType('')} className="text-sm text-gray-400 hover:text-gray-600">Clear</button>}
            <div className="flex-1" />
            <p className="text-gray-400 text-sm">{zones.length} zones</p>
            <button onClick={() => setShowNewZoneModal(true)}
              className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90">
              + New Zone
            </button>
          </div>
          {zonesLoading ? <p className="text-gray-400 text-sm">Loading zones...</p>
          : zones.length === 0 ? <p className="text-gray-400 text-sm">No zones yet.</p>
          : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Label','Gender','Size','Type','Bins','Items','Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-gray-500 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {zones.map(zone => (
                    <tr key={zone.zone_id} onClick={() => handleSelectZone(zone)}
                      className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors">
                      <td className="px-4 py-3 font-semibold text-gray-800">{zone.label}</td>
                      <td className="px-4 py-3 text-gray-600 capitalize">{zone.gender}</td>
                      <td className="px-4 py-3 text-gray-600">{zone.size}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium
                          ${zone.zone_type === 'primary_storage' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-zoddle-blue'}`}>
                          {zone.zone_type === 'primary_storage' ? 'Primary' : 'Mini-Pack'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{getBinCountForZone(zone.zone_id)}</td>
                      <td className="px-4 py-3 text-gray-600">{getItemCountForZone(zone.zone_id)}</td>
                      <td className="px-4 py-3">
                        <button onClick={(e) => { e.stopPropagation(); handleDeactivateZone(zone.zone_id, zone.label); }}
                          className="text-xs text-gray-400 hover:text-zoddle-pink transition-colors">
                          Deactivate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'bins' && (
        <div>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <select value={filterZone} onChange={(e) => setFilterZone(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">All Zones</option>
              {zones.map(z => <option key={z.zone_id} value={z.zone_id}>{z.label}</option>)}
            </select>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-600 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">All Types</option>
              {BIN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {(filterZone || filterType) && (
              <button onClick={() => { setFilterZone(''); setFilterType(''); }} className="text-sm text-gray-400 hover:text-gray-600">Clear filters</button>
            )}
            <div className="flex-1" />
            <p className="text-gray-400 text-sm">{bins.length} bins</p>
            <button onClick={() => { setNewBin({ label: '', bin_type: 'primary_storage', zone_id: filterZone || '', capacity: 50 }); setShowNewBinModal(true); }}
              className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90">
              + New Bin
            </button>
          </div>
          {binsLoading ? <p className="text-gray-400 text-sm">Loading bins...</p>
          : bins.length === 0 ? <p className="text-gray-400 text-sm">No bins found.</p>
          : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Label','Type','Zone','Occupancy','Actions'].map(h => (
                      <th key={h} className={`text-left px-4 py-3 text-gray-500 font-semibold ${h === 'Occupancy' ? 'w-44' : ''}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bins.map(bin => (
                    <tr key={bin.bin_id} onClick={() => handleSelectBin(bin)}
                      className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors">
                      <td className="px-4 py-3 font-semibold text-gray-800">{bin.label}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                          {BIN_TYPES.find(t => t.value === bin.bin_type)?.label || bin.bin_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{bin.zone_id ? getZoneLabel(bin.zone_id) : '—'}</td>
                      <td className="px-4 py-3"><OccupancyBar current={bin.current_occupancy || 0} capacity={bin.capacity} /></td>
                      <td className="px-4 py-3">
                        <button onClick={(e) => { e.stopPropagation(); handleDeactivateBin(bin.bin_id, bin.label); }}
                          className="text-xs text-gray-400 hover:text-zoddle-pink transition-colors">
                          Deactivate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showNewZoneModal && (
        <Modal title="Create New Zone" onClose={() => { setShowNewZoneModal(false); setZoneError(''); }}>
          <div className="space-y-4">
            {zoneRows.map((row, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500">Zone {i + 1}</span>
                  {zoneRows.length > 1 && <button onClick={() => removeZoneRow(i)} className="text-gray-400 hover:text-zoddle-pink text-sm">Remove</button>}
                </div>
                {[['Gender','gender',[['girl','Girl'],['boy','Boy'],['unisex','Unisex']]],
                  ['Size','size', zoddleSizes.map(s => [s,s])],
                  ['Zone Type','zone_type',[['primary_storage','Primary Storage'],['mini_pack_storage','Mini-Pack Storage']]]
                ].map(([label, field, options]) => (
                  <div key={field}>
                    <label className="block text-gray-600 text-xs mb-1">{label}</label>
                    <select value={row[field]} onChange={(e) => updateZoneRow(i, field, e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:border-zoddle-green">
                      {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            ))}
            <button onClick={addZoneRow}
              className="w-full text-sm text-zoddle-blue border border-zoddle-blue rounded-lg py-2 hover:bg-blue-50 transition-colors font-semibold">
              + Add Another Zone
            </button>
            {zoneError && <p className="text-zoddle-pink text-sm">{zoneError}</p>}
            <button onClick={handleCreateZones} disabled={zoneSubmitting}
              className="w-full bg-zoddle-green text-white font-bold py-3 rounded-lg hover:opacity-90 disabled:opacity-50">
              {zoneSubmitting ? 'Creating...' : `Create ${zoneRows.length} Zone${zoneRows.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </Modal>
      )}

      {showNewBinModal && (
        <Modal title="Create New Bin" onClose={() => { setShowNewBinModal(false); setBinError(''); }}>
          <div className="space-y-4">
            {[['Bin Label','label','text','e.g. A1-01'],['Capacity','capacity','number','50']].map(([label, field, type, placeholder]) => (
              <div key={field}>
                <label className="block text-gray-600 text-sm mb-1">{label}</label>
                <input type={type} value={newBin[field]} placeholder={placeholder}
                  onChange={(e) => setNewBin({ ...newBin, [field]: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-gray-50 focus:outline-none focus:border-zoddle-green" />
              </div>
            ))}
            <div>
              <label className="block text-gray-600 text-sm mb-1">Bin Type</label>
              <select value={newBin.bin_type} onChange={(e) => setNewBin({ ...newBin, bin_type: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:border-zoddle-green">
                {BIN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">Zone <span className="text-gray-400">(optional)</span></label>
              <select value={newBin.zone_id} onChange={(e) => setNewBin({ ...newBin, zone_id: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:border-zoddle-green">
                <option value="">No Zone</option>
                {zones.map(z => <option key={z.zone_id} value={z.zone_id}>{z.label}</option>)}
              </select>
            </div>
            {binError && <p className="text-zoddle-pink text-sm">{binError}</p>}
            <button onClick={handleCreateBin} disabled={binSubmitting || !newBin.label}
              className="w-full bg-zoddle-green text-white font-bold py-3 rounded-lg hover:opacity-90 disabled:opacity-50">
              {binSubmitting ? 'Creating...' : 'Create Bin'}
            </button>
          </div>
        </Modal>
      )}

      {selectedBin && activeTab === 'bins' && (
        <Modal title={`Items in ${selectedBin.label}`} onClose={() => setSelectedBin(null)}>
          {binItemsLoading ? <p className="text-gray-400 text-sm">Loading items...</p>
          : binItems.length === 0 ? <p className="text-gray-400 text-sm">This bin is empty.</p>
          : (
            <div className="space-y-2">
              {binItems.map(item => (
                <div key={item.item_id} className="bg-gray-50 rounded-lg px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-800 text-sm">{item.design_name}</span>
                    <span className="text-xs text-gray-500">{item.size}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-400">{item.zoddle_barcode}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                      ${item.condition === 'good' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                      {item.condition}
                    </span>
                    <span className="text-xs text-gray-500">₹{item.zoddle_price}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
